import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, CheckCircle2, ClipboardCheck,
  Mail, Sparkles,
} from 'lucide-react';

import api from '../services/api';


const INITIAL_PROFILE = {
  establishment_type: '',
  consultory_count: 1,
  processing_type: '',
  radiology_type: '',
};

const READINESS_OPTIONS = [
  { value: 'yes', label: 'Sim, está organizado', description: 'Há rotina definida e registros disponíveis.' },
  { value: 'partial', label: 'Em parte', description: 'Já começamos, mas ainda existem pendências.' },
  { value: 'no', label: 'Ainda não', description: 'Esse ponto ainda precisa ser estruturado.' },
  { value: 'unknown', label: 'Não sei informar', description: 'Não tenho segurança para avaliar agora.' },
];

const QUESTIONS = [
  {
    id: 'establishment_type', kind: 'profile', title: 'Como o serviço está organizado?',
    subtitle: 'Escolha a opção que mais se aproxima da realidade atual.',
    options: [
      ['individual_office', 'Consultório individual'], ['clinic', 'Clínica odontológica'],
      ['collective_office', 'Consultório coletivo'], ['mobile_service', 'Atendimento móvel ou portátil'],
      ['other', 'Outro tipo de serviço'],
    ],
  },
  {
    id: 'consultory_count', kind: 'number', title: 'Quantos consultórios individuais existem no estabelecimento?',
    subtitle: 'Considere os ambientes onde ocorre assistência odontológica.',
  },
  {
    id: 'processing_type', kind: 'profile', title: 'Como é feito o processamento dos instrumentais?',
    subtitle: 'Essa resposta altera as orientações apresentadas no relatório.',
    options: [
      ['internal', 'Na própria clínica'], ['outsourced', 'Por serviço terceirizado'],
      ['not_applicable', 'Não se aplica ao serviço'],
    ],
  },
  {
    id: 'radiology_type', kind: 'profile', title: 'A clínica utiliza radiologia odontológica?',
    subtitle: 'Selecione a opção que corresponde aos equipamentos utilizados.',
    options: [
      ['none', 'Não utiliza'], ['intraoral', 'Somente intraoral'],
      ['extraoral', 'Somente extraoral'], ['both', 'Intraoral e extraoral'],
    ],
  },
  {
    id: 'sanitary_documents', kind: 'readiness', pillar: 'RDC 1.002/2025',
    title: 'Os documentos sanitários, planos, protocolos e POPs estão organizados e atualizados?',
  },
  {
    id: 'sterilization_traceability', kind: 'readiness', pillar: 'RDC 1.002/2025',
    title: 'O processamento dos instrumentais possui rotina definida e registros rastreáveis?',
  },
  {
    id: 'equipment_control', kind: 'readiness', pillar: 'RDC 1.002/2025',
    title: 'Manutenções, calibrações, qualificações e laudos dos equipamentos são controlados?',
  },
  {
    id: 'training_records', kind: 'readiness', pillar: 'RDC 1.002/2025',
    title: 'As capacitações da equipe têm planejamento e evidências de realização?',
  },
  {
    id: 'patient_safety', kind: 'readiness', pillar: 'RDC 1.002/2025',
    title: 'A clínica possui rotinas para segurança do paciente, incidentes e ações corretivas?',
  },
  {
    id: 'radiology_documents', kind: 'readiness', pillar: 'RDC 1.002/2025', radiologyOnly: true,
    title: 'Os documentos e controles de proteção radiológica estão atualizados?',
  },
  {
    id: 'clinical_record', kind: 'readiness', pillar: 'Manual do Prontuário do CFO',
    title: 'Prontuários, planos de tratamento, evoluções e intercorrências seguem um padrão completo?',
  },
  {
    id: 'consent_documents', kind: 'readiness', pillar: 'Manual do Prontuário do CFO',
    title: 'TCLE, contratos, exames, imagens e recibos de entrega documental ficam organizados?',
  },
  {
    id: 'patient_information', kind: 'readiness', pillar: 'Direitos do Paciente',
    title: 'Riscos, benefícios, alternativas, custos e decisões do paciente são informados e registrados?',
  },
  {
    id: 'record_access', kind: 'readiness', pillar: 'Direitos do Paciente',
    title: 'Existe um fluxo seguro para acesso, cópia e entrega da documentação ao paciente?',
  },
];

const LEVEL_STYLES = {
  initial: 'bg-amber-50 text-amber-800 border-amber-200',
  organizing: 'bg-blue-50 text-blue-800 border-blue-200',
  advanced: 'bg-emerald-50 text-emerald-800 border-emerald-200',
};

function Choice({ active, label, description, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full rounded-xl border p-4 text-left transition ${active ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200' : 'border-gray-200 bg-white hover:border-indigo-200'}`}>
      <span className="flex items-start gap-3">
        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${active ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300'}`}>
          {active && <Check className="h-3.5 w-3.5" />}
        </span>
        <span>
          <span className="block font-medium text-gray-900">{label}</span>
          {description && <span className="mt-1 block text-sm text-gray-500">{description}</span>}
        </span>
      </span>
    </button>
  );
}

export default function PublicComplianceAssessmentPage() {
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(INITIAL_PROFILE);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', whatsapp: '', privacy_acknowledged: false, marketing_consent: false });
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState('');

  const questions = useMemo(
    () => QUESTIONS.filter((question) => !question.radiologyOnly || profile.radiology_type !== 'none'),
    [profile.radiology_type]
  );
  const question = questions[step];
  const progress = Math.round(((step + 1) / questions.length) * 100);

  const currentValue = question?.kind === 'readiness' ? answers[question.id] : profile[question?.id];
  const canContinue = question?.kind === 'number'
    ? Number(profile.consultory_count) >= 1 && Number(profile.consultory_count) <= 100
    : Boolean(currentValue);

  const payload = () => ({
    profile: { ...profile, consultory_count: Number(profile.consultory_count) },
    answers,
  });

  const choose = (value) => {
    setError('');
    if (question.kind === 'readiness') setAnswers((current) => ({ ...current, [question.id]: value }));
    else setProfile((current) => ({ ...current, [question.id]: value }));
  };

  const goNext = async () => {
    if (!canContinue || loading) return;
    if (step < questions.length - 1) {
      setStep((current) => current + 1);
      return;
    }
    setLoading(true); setError('');
    try {
      const response = await api.post('/marketing/compliance-assessment/preview', payload());
      setResult(response.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || 'Não foi possível preparar o resultado. Tente novamente.');
    } finally { setLoading(false); }
  };

  const submitLead = async (event) => {
    event.preventDefault();
    if (!form.privacy_acknowledged || loading) return;
    setLoading(true); setError('');
    try {
      const response = await api.post('/marketing/compliance-assessment/lead', {
        ...payload(), ...form,
        assessment_id: result.assessment_id,
        source: 'rdc1002_assessment',
        utm_source: searchParams.get('utm_source'),
        utm_medium: searchParams.get('utm_medium'),
        utm_campaign: searchParams.get('utm_campaign'),
      });
      setSaved(response.data);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || 'Não foi possível enviar o relatório. Tente novamente.');
    } finally { setLoading(false); }
  };

  const restart = () => {
    setProfile(INITIAL_PROFILE); setAnswers({}); setStep(0); setResult(null); setSaved(null); setError('');
  };

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900">
      <header className="border-b border-gray-100 bg-white">
        <div className="mx-auto flex h-24 max-w-5xl items-center justify-between px-4">
          <Link to="/" className="flex items-center">
            <img src="/marketing/logo_official.png" alt="OdontoAssist AI" className="h-24 w-auto object-contain" />
          </Link>
          <Link to="/login" className="text-sm font-semibold text-gray-600 hover:text-indigo-600">Entrar</Link>
        </div>
      </header>

      {!result ? (
        <main className="mx-auto max-w-3xl px-4 py-8 sm:py-14">
          <div className="mb-6 text-center">
            <span className="inline-flex rounded-full bg-indigo-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-indigo-700">Diagnóstico gratuito · cerca de 3 minutos</span>
            <h1 className="mt-4 text-2xl font-bold sm:text-4xl">Quão preparada está sua clínica para as novas regras de 2026?</h1>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base">Uma avaliação orientativa sobre a RDC nº 1.002/2025, o Manual do Prontuário do CFO e o Estatuto dos Direitos do Paciente.</p>
          </div>

          <section className="rounded-2xl bg-white p-5 shadow-sm sm:p-8">
            <div className="mb-7">
              <div className="mb-2 flex items-center justify-between text-xs font-medium"><span className="text-indigo-700">Uma pergunta por vez</span><span className="text-gray-500">{step + 1} de {questions.length}</span></div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-indigo-600 transition-all" style={{ width: `${progress}%` }} /></div>
            </div>

            {question.pillar && <span className="text-xs font-bold uppercase tracking-wide text-indigo-600">{question.pillar}</span>}
            <h2 className="mt-2 text-xl font-semibold leading-7 sm:text-2xl">{question.title}</h2>
            {question.subtitle && <p className="mt-2 text-sm text-gray-500">{question.subtitle}</p>}

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {question.kind === 'number' ? (
                <label className="sm:col-span-2">
                  <span className="text-sm font-medium text-gray-700">Quantidade</span>
                  <input type="number" min="1" max="100" value={profile.consultory_count}
                    onChange={(event) => setProfile((current) => ({ ...current, consultory_count: event.target.value }))}
                    className="mt-2 block w-36 rounded-xl border border-gray-300 px-4 py-3 text-lg focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100" />
                </label>
              ) : (question.kind === 'readiness' ? READINESS_OPTIONS : question.options.map(([value, label]) => ({ value, label }))).map((option) => (
                <Choice key={option.value} active={currentValue === option.value} label={option.label} description={option.description} onClick={() => choose(option.value)} />
              ))}
            </div>

            {error && <p className="mt-5 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
            <div className="mt-8 flex justify-between border-t pt-5">
              <button type="button" onClick={() => setStep((current) => Math.max(0, current - 1))} disabled={step === 0}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 disabled:invisible"><ArrowLeft size={16} /> Voltar</button>
              <button type="button" onClick={goNext} disabled={!canContinue || loading}
                className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
                {step === questions.length - 1 ? (loading ? 'Preparando...' : 'Ver meu resultado') : 'Continuar'}
                {step === questions.length - 1 ? <Sparkles size={16} /> : <ArrowRight size={16} />}
              </button>
            </div>
          </section>
          <p className="mt-4 text-center text-xs leading-5 text-gray-500">Não solicitamos dados de pacientes. O diagnóstico é orientativo e não certifica conformidade.</p>
        </main>
      ) : (
        <main className="mx-auto max-w-4xl px-4 py-8 sm:py-14">
          <section className="rounded-2xl bg-white p-5 shadow-sm sm:p-8">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <span className={`inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${LEVEL_STYLES[result.level]}`}>{result.level_label}</span>
                <h1 className="mt-4 text-3xl font-bold">Seu índice orientativo é {result.score}%</h1>
                <p className="mt-3 max-w-2xl leading-7 text-gray-600">{result.summary}</p>
              </div>
              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-2xl font-bold text-indigo-700 ring-8 ring-indigo-100">{result.score}%</div>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {result.pillars.map((pillar) => <div key={pillar.id} className="rounded-xl border border-gray-200 p-4"><div className="text-sm font-medium text-gray-600">{pillar.label}</div><div className="mt-2 text-2xl font-bold text-indigo-700">{pillar.score}%</div></div>)}
            </div>

            <div className="mt-8 border-t pt-7">
              <h2 className="flex items-center gap-2 text-xl font-semibold"><ClipboardCheck className="text-indigo-600" /> Prioridades sugeridas</h2>
              <div className="mt-4 space-y-3">
                {result.priorities.length ? result.priorities.slice(0, 3).map((item, index) => (
                  <div key={item.id} className="flex gap-3 rounded-xl bg-slate-50 p-4"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">{index + 1}</span><div><h3 className="font-semibold">{item.title}</h3><p className="mt-1 text-sm leading-6 text-gray-600">{item.recommendation}</p></div></div>
                )) : <div className="rounded-xl bg-emerald-50 p-4 text-emerald-800">Continue revisando os registros e mantendo as evidências atualizadas.</div>}
              </div>
            </div>

            <div className="mt-8 rounded-2xl bg-slate-900 p-5 text-white sm:p-7">
              <span className="text-xs font-bold uppercase tracking-wide text-indigo-300">Do diagnóstico para a rotina</span>
              <h2 className="mt-2 text-2xl font-bold">Como o OdontoAssist AI pode ajudar</h2>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
                O resultado mostra onde começar. Na plataforma, essas necessidades são organizadas em recursos simples para que a clínica acompanhe documentos, registros e prazos sem depender de várias planilhas e pastas separadas.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {[
                  {
                    title: 'Prontuário e direitos do paciente',
                    description: 'Prontuário, evolução clínica, TCLE, contratos, exames, imagens, assinatura e registro da entrega de documentos.',
                  },
                  {
                    title: 'Boas Práticas da clínica',
                    description: 'Plano guiado da RDC 1.002, documentos e versões, esterilização, equipamentos, capacitações, rotinas e ocorrências.',
                  },
                  {
                    title: 'Prazos e evidências',
                    description: 'Lembretes opcionais, históricos e um resumo consolidado para facilitar o acompanhamento e a preparação para fiscalização.',
                  },
                ].map((item) => (
                  <div key={item.title} className="rounded-xl bg-white/10 p-4 ring-1 ring-white/10">
                    <CheckCircle2 className="text-indigo-300" size={20} />
                    <h3 className="mt-3 font-semibold">{item.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-300">{item.description}</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs leading-5 text-slate-400">
                Quando uma adequação depende de obra, laudo, fornecedor ou decisão do responsável técnico, o OdontoAssist não executa o serviço: ele ajuda a registrar, acompanhar e manter as evidências organizadas.
              </p>
            </div>

            <div className="mt-6 rounded-2xl border border-indigo-200 bg-white p-5 text-center sm:p-6">
              <h2 className="text-xl font-semibold text-gray-900">Quer conhecer essas soluções na prática?</h2>
              <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-gray-600">Você não precisa enviar o relatório por e-mail. Pode experimentar agora o OdontoAssist AI gratuitamente e conhecer os recursos apresentados acima.</p>
              <Link to={`/login?trial=1&assessment=${result.assessment_id}`} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-3 font-semibold text-white hover:bg-indigo-700">Experimentar gratuitamente <ArrowRight size={17} /></Link>
              <p className="mt-3 text-xs text-gray-500">7 dias grátis, sem cartão de crédito, sem fidelidade e sem compromisso.</p>
            </div>

            {!saved ? (
              <form onSubmit={submitLead} className="mt-8 rounded-2xl bg-indigo-50 p-5 sm:p-6">
                <div className="flex items-start gap-3"><Mail className="mt-1 shrink-0 text-indigo-700" /><div><h2 className="text-lg font-semibold">Prefere guardar o resultado?</h2><p className="mt-1 text-sm text-gray-600">O envio por e-mail é opcional. Preencha os dados abaixo somente se quiser receber uma cópia deste diagnóstico.</p></div></div>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <label className="text-sm font-medium text-gray-700">Nome<input required minLength="2" maxLength="120" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 font-normal" /></label>
                  <label className="text-sm font-medium text-gray-700">E-mail<input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 font-normal" /></label>
                  <label className="text-sm font-medium text-gray-700 sm:col-span-2">WhatsApp <span className="font-normal text-gray-500">(opcional)</span><input maxLength="30" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 font-normal" /></label>
                </div>
                <label className="mt-5 flex items-start gap-2 text-xs leading-5 text-gray-600"><input required type="checkbox" checked={form.privacy_acknowledged} onChange={(e) => setForm({ ...form, privacy_acknowledged: e.target.checked })} className="mt-1" /> Estou ciente de que meus dados serão usados para gerar e enviar este diagnóstico, conforme a <Link to="/privacidade" className="text-indigo-700 underline">Política de Privacidade</Link>.</label>
                <label className="mt-3 flex items-start gap-2 text-xs leading-5 text-gray-600"><input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-1" /> Quero receber conteúdos e novidades do OdontoAssist AI. Esta autorização é opcional.</label>
                {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
                <button type="submit" disabled={!form.privacy_acknowledged || loading} className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-3 font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">{loading ? 'Enviando...' : 'Enviar meu relatório'} <ArrowRight size={17} /></button>
              </form>
            ) : (
              <div className="mt-8 rounded-2xl bg-emerald-50 p-6 text-center">
                <CheckCircle2 className="mx-auto text-emerald-600" size={36} /><h2 className="mt-3 text-xl font-semibold text-emerald-900">Diagnóstico salvo</h2>
                <p className="mt-2 text-sm text-emerald-800">{saved.email_sent ? 'Enviamos o relatório para o seu e-mail.' : 'Seu resultado foi salvo. Se o e-mail não chegar, você ainda pode conhecer as soluções abaixo.'}</p>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-6 text-gray-700">Experimente o OdontoAssist gratuitamente e veja como prontuários, documentos, Boas Práticas, esterilização, equipamentos e lembretes podem ficar organizados em uma única plataforma.</p>
                <Link to={`/login?trial=1&assessment=${saved.assessment_id}`} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-3 font-semibold text-white hover:bg-indigo-700">Experimentar gratuitamente <ArrowRight size={17} /></Link>
                <p className="mt-3 text-xs text-gray-500">7 dias grátis, sem cartão de crédito e sem fidelidade.</p>
              </div>
            )}

            <div className="mt-6 flex flex-col items-center justify-between gap-3 border-t pt-5 sm:flex-row">
              <p className="max-w-2xl text-xs leading-5 text-gray-500">{result.disclaimer}</p>
              <button type="button" onClick={restart} className="shrink-0 text-sm font-medium text-indigo-700 hover:underline">Refazer diagnóstico</button>
            </div>
          </section>
        </main>
      )}
    </div>
  );
}
